import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';
import BotonGenerarAmparoPnc from '../../components/AmparoPnc/BotonGenerarAmparoPnc';
import TarjetaTurnoAnses from '../../components/TarjetaTurnoAnses';
import ExpedientesAnsesTramite, { enSeguimientoDeExpediente } from '../../components/ExpedientesAnsesTramite';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import { getTramiteAmparoPncById, actualizarChecklistItem, actualizarTramiteAmparoPnc, actualizarComentariosAmparoPnc } from '../../services/amparoPncService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import EtiquetaTramite from '../../components/EtiquetaTramite';

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

// Clase del badge contador (X/Y): verde cuando está completo, gris en caso contrario.
const claseBadgeContador = (completados, total) => (total > 0 && completados === total ? 'bg-success' : 'bg-secondary');

function VistaDetalladaTramiteAmparoPnc() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edición de datos del trámite (no el cliente)
    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    // Herramienta: crear turno prellenado con los datos del cliente del trámite
    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    // Archivos del trámite (fuente única: el checklist enlaza por idArchivo; "otros" son los no vinculados)
    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteAmparoPncById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Amparo PNC:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteAmparoPncById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM/Reconocimiento/PNC)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-amparo-pnc' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const formatearFechaEvento = (fechaString) => {
        if (!fechaString) return '';
        const [y, m, d] = fechaString.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    // ---- Descarga con nombre real (extensión + tipo correctos) ----
    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    // ---- Checklist ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return; // con archivo adjunto, el ítem queda marcado y bloqueado
        try {
            await actualizarChecklistItem(item.id, { presentado: !item.presentado, idArchivo: null });
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivo = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistItem(item.id, { presentado: true, idArchivo });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjunto = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistItem(item.id, { presentado: false, idArchivo: null });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    // ---- Otros documentos (no vinculados al checklist) ----
    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Comentarios (nota editable con firma, igual que ART/RTI/Jubilación/PUAM) ----
    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosAmparoPnc(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    // ---- Editar datos del trámite ----
    // Amparo PNC no tiene subtipo: el checklist es siempre el mismo, así que editar estos campos
    // nunca necesita regenerar/confirmar el checklist (patrón PUAM/PNC).
    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            cmoIncorporado: !!tramite.cmoIncorporado,
            tieneCopiaCmo: !!tramite.tieneCopiaCmo,
            tieneCud: !!tramite.tieneCud,
            fechaInicioTramitePnc: formatLocalFecha(tramite.fechaInicioTramitePnc),
            envioProntoDespacho: formatLocalFecha(tramite.envioProntoDespacho),
            nroExpedientePnc: tramite.nroExpedientePnc || '',
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async () => {
        setGuardandoDatos(true);
        try {
            await actualizarTramiteAmparoPnc(idTramite, {
                ...tramiteEdit,
                organismo: tramiteEdit.organismo || null,
                fechaInicioTramitePnc: tramiteEdit.fechaInicioTramitePnc || null,
                envioProntoDespacho: tramiteEdit.envioProntoDespacho || null,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            console.error('Error al guardar datos:', error);
            toast.error(error.response?.data || 'No se pudieron guardar los datos');
        } finally {
            setGuardandoDatos(false);
        }
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    // Amparo Ley 16.986 (V256) usa esta misma ficha: cambian el nombre y la carátula.
    const esLey16986 = String(tramite?.tipoTramite?.nombre || '').toUpperCase() === 'AMPARO_LEY_16986';
    const nombreTipo = esLey16986 ? 'Amparo Ley 16.986' : 'Amparo PNC';

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold text-primary mb-0">Detalles del Trámite de {nombreTipo}</h2>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill fs-6 px-3">
                            {nombreTipo.toUpperCase()}
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <BotonGenerarCaratula
                        tipo={esLey16986 ? 'AMPARO_LEY_16986' : 'AMPARO_PNC'}
                        idTramite={idTramite}
                        onGenerado={recargarArchivos}
                    />
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            {/* El escrito es el del amparo PNC por mora: no corresponde al Amparo Ley 16.986. */}
            {!esLey16986 && (
                <div className="mb-3">
                    <BotonGenerarAmparoPnc
                        idTramite={idTramite}
                        datos={{ organismo: tramite.organismo, tieneCopiaCmo: tramite.tieneCopiaCmo }}
                        onGenerado={recargarArchivos}
                    />
                </div>
            )}

            {/* Advertencia si no tiene CMO incorporado — no bloqueante */}
            {!tramite.cmoIncorporado && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    El CMO no fue incorporado con anterioridad — verificar antes de presentar el amparo.
                </div>
            )}

            <BannerEstadoFinal
                idTramite={tramite.idTramite}
                nombreEstadoActual={tramite.nombreEstadoActual}
            />

            {/* Estado + historial */}
            <div
                className="card shadow-sm border-0 rounded-4 p-4 mb-3"
                style={{ backgroundColor: '#f8f9fa', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onClick={() => setModalHistorialAbierto(true)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                            {tramite.nombreEstadoActual || 'Ingreso del Trámite'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <span className="text-primary">
                        <i className="bi bi-arrow-right-circle me-1"></i>Ver historial
                    </span>
                </div>
            </div>

            {/* Nota editable de comentarios (con firma, igual que ART/RTI/Jubilación/PUAM) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label={`Comentarios del Trámite de ${nombreTipo}`}
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Amparo Ley 16.986: turno en ANSES como en el resto de los trámites (presentar de nuevo
                la pensión antes de la demanda). Al cargarlo se genera la citación al estudio y el
                evento de agenda. */}
            {esLey16986 && (
                <TarjetaTurnoAnses
                    idTramite={tramite.idTramite}
                    cuilCliente={tramite.idCliente || cliente.cuil}
                    nombreArea="este Amparo Ley 16.986"
                    nombreEstadoActual={tramite.nombreEstadoActual}
                    onCambio={recargarTramite}
                    onEstadoCambiado={recargarTramite}
                />
            )}

            {enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">Eventos del Trámite</h4>
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold text-primary mb-0">{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge bg-primary rounded-pill">{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2">{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="text-success fw-semibold">Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Info del Trámite + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold text-primary mb-3">
                            <i className="bi bi-person-vcard me-2"></i>
                            Información del Trámite
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Organismo:</strong> {tramite.organismo === 'ANSES' ? 'ANSES' : tramite.organismo === 'PROVINCIA' ? 'Provincia' : tramite.organismo === 'ANDIS' ? 'ANDIS' : 'Sin especificar'}</p>
                                <p><strong>CMO incorporado con anterioridad:</strong> {tramite.cmoIncorporado ? 'Sí' : 'No'}</p>
                                <p><strong>Tiene copia del CMO:</strong> {tramite.tieneCopiaCmo ? 'Sí' : 'No'}</p>
                                <p><strong>Tiene CUD:</strong> {tramite.tieneCud ? 'Sí' : 'No'}</p>
                                <p><strong>Fecha de inicio del trámite PNC:</strong> {tramite.fechaInicioTramitePnc ? new Date(tramite.fechaInicioTramitePnc + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                <p><strong>Día que se envió el pronto despacho:</strong> {tramite.envioProntoDespacho ? new Date(tramite.envioProntoDespacho + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                <p><strong>N° de expediente PNC:</strong> {tramite.nroExpedientePnc || 'Sin especificar'}</p>
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Organismo</label>
                                    <select className="form-select" value={tramiteEdit.organismo || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, organismo: e.target.value }))}>
                                        <option value="">Sin especificar</option>
                                        <option value="ANSES">ANSES</option>
                                        <option value="PROVINCIA">Provincia</option>
                                        <option value="ANDIS">ANDIS</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Fecha de inicio del trámite PNC</label>
                                    <input type="date" className="form-control" value={tramiteEdit.fechaInicioTramitePnc || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, fechaInicioTramitePnc: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">N° de expediente PNC</label>
                                    <input type="text" className="form-control" value={tramiteEdit.nroExpedientePnc || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, nroExpedientePnc: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Día que se envió el pronto despacho</label>
                                    <input type="date" className="form-control" value={tramiteEdit.envioProntoDespacho || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, envioProntoDespacho: e.target.value }))} />
                                </div>
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-cmo-incorporado"
                                        checked={!!tramiteEdit.cmoIncorporado}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, cmoIncorporado: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-cmo-incorporado">CMO incorporado con anterioridad</label>
                                </div>
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-tiene-copia-cmo"
                                        checked={!!tramiteEdit.tieneCopiaCmo}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, tieneCopiaCmo: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-tiene-copia-cmo">Tiene copia del CMO</label>
                                </div>
                                <div className="form-check mb-3">
                                    <input className="form-check-input" type="checkbox" id="edit-tiene-cud"
                                        checked={!!tramiteEdit.tieneCud}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, tieneCud: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-tiene-cud">Tiene CUD (Certificado Único de Discapacidad)</label>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleGuardarDatos} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expedientes de ANSES: acá, debajo de los datos del trámite y del cliente; arriba solo
                mientras el trámite está en "Seguimiento de Expediente". */}
            {!enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Documentación del Trámite: checklist de formularios + otros documentos (un solo lugar) */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold text-primary mb-3"><i className="bi bi-folder2-open me-2"></i>Documentación del Trámite</h4>

                        {/* Formularios requeridos */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-check2-square me-2"></i>Formularios requeridos</h6>
                            <span className={`badge ${claseBadgeContador(formularios.filter(f => f.presentado).length, formularios.length)}`}>{formularios.filter(f => f.presentado).length}/{formularios.length}</span>
                        </div>
                        {formularios.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                                <p className="mb-0">No hay formularios en el checklist.</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)}
                                                        title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>
                                                        {nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                        onClick={() => quitarAdjunto(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id
                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => adjuntarArchivo(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <hr className="my-4" />

                        {/* Otros documentos (no vinculados a un formulario) */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-paperclip me-2"></i>Otros documentos</h6>
                            <label className="btn btn-sm btn-outline-primary rounded-pill mb-0">
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay otros documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text text-primary" style={{ fontSize: '1.25rem' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            {/* Acciones irreversibles (cambiar tipo / eliminar definitivamente).
                El propio panel decide si mostrarse segun el rol. */}
            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="AMPARO_PNC"
            />

            {/* Modal historial de estados (ámbito AMPARO_PNC) */}
            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="AMPARO_PNC"
            />

            {/* Modal crear turno (reutiliza el turnero de Recepción, prellenado con el cliente) */}
            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarModalTurno(false)}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            celdaSel={null}
                            valoresIniciales={{
                                nombre: cliente.nombre || '',
                                apellido: cliente.apellido || '',
                                cuil: cliente.cuil || '',
                                telefono: cliente.telefonoPropio || '',
                                tipoTramite: 'AMPARO_PNC',
                            }}
                            onCerrar={() => setMostrarModalTurno(false)}
                            onCreado={() => { setMostrarModalTurno(false); toast.success('Turno creado correctamente'); }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteAmparoPnc;
